<template>
	<div class="benefit-detail-page">
		<div class="benefit-page-breadcrumb">
			<el-breadcrumb separator-class="el-icon-arrow-right">
				<el-breadcrumb-item :to="{ name: 'MyBenefits' }">我的权益</el-breadcrumb-item>
				<el-breadcrumb-item>剩余权益</el-breadcrumb-item>
			</el-breadcrumb>
		</div>

		<benefit-order-summary :order="summaryOrder">
			<el-button slot="extra" class="summary-entry-button" icon="el-icon-time" @click="viewUsageRecord">
				权益使用记录
			</el-button>
		</benefit-order-summary>

		<div v-loading="loading" class="benefit-detail-section">
			<div class="detail-section-heading">
				<strong>权益清单</strong>
				<span>共 {{ benefitList.length }} 项功能权益，额度按当前账号全部订单汇总</span>
			</div>
			<el-table class="benefit-table" :data="benefitList" empty-text="暂无权益数据">
				<el-table-column label="权益板块" width="96" align="center">
					<template slot-scope="scope">
						<span class="module-tag">{{ scope.row.module }}</span>
					</template>
				</el-table-column>
				<el-table-column label="权益类型" min-width="155">
					<template slot-scope="scope">
						<div class="benefit-type-cell">
							<span class="benefit-type-icon"><i class="el-icon-medal"></i></span>
							<span class="benefit-type-name">{{ scope.row.type }}</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="权益说明" min-width="260">
					<template slot-scope="scope">
						<span class="benefit-description">{{ scope.row.description }}</span>
					</template>
				</el-table-column>
				<el-table-column label="累计权益" width="130" align="center">
					<template slot-scope="scope">
						<div class="cumulative-quota">
							<span class="quota-value">{{ scope.row.total }}</span>
							<small>本订单 {{ scope.row.sourceTotal }}</small>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="到期时间" width="148" align="center">
					<template slot-scope="scope">
						<div class="benefit-time" :class="{ 'is-long-term': !scope.row.expireClock }">
							<i class="el-icon-date"></i>
							<div>
								<span>{{ scope.row.expireDate }}</span>
								<small v-if="scope.row.expireClock">{{ scope.row.expireClock }}</small>
							</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="使用情况" min-width="140">
					<template slot-scope="scope">
						<div v-if="scope.row.periodBenefit" class="period-status">
							<span class="status-dot"></span>
							<span>{{ scope.row.usage }}</span>
						</div>
						<div v-else class="usage-progress">
							<div class="usage-progress-text">
								<span>{{ scope.row.usage }}</span>
								<small>{{ scope.row.percentage }}%</small>
							</div>
							<el-progress :percentage="scope.row.percentage" :stroke-width="6" :show-text="false"></el-progress>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="剩余权益" width="150" align="center">
					<template slot-scope="scope">
						<div class="remaining-value">
							<strong>{{ scope.row.remainingPrimary }}</strong>
							<small>{{ scope.row.remainingSecondary }}</small>
						</div>
					</template>
				</el-table-column>
			</el-table>
		</div>
	</div>
</template>

<script>
	import BenefitOrderSummary from './components/BenefitOrderSummary'
	import { queryMyGoodsBenefitDetail } from '@/api/mall'

	export default {
		name: 'MyBenefitRemaining',
		components: { BenefitOrderSummary },
		data() {
			return {
				loading: false,
				benefitRecords: [],
				sourceBenefitRecords: [],
				useRecords: []
			}
		},
		computed: {
			currentOrder() {
				return {
					orderId: this.$route.query.orderId,
					orderNo: this.$route.query.orderNo || '-',
					orderTime: this.$route.query.orderTime || '-',
					shipmentTime: this.$route.query.shipmentTime || '-'
				}
			},
			currentProduct() {
				return {
					id: this.$route.query.productId,
					productName: this.$route.query.productName || '商品权益',
					productDesc: this.$route.query.productDesc || '',
					quantity: Number(this.$route.query.quantity || 1),
					status: this.$route.query.status || 'pending',
					benefitCount: Number(this.$route.query.benefitCount || this.benefitRecords.length)
				}
			},
			// 摘要组件只展示当前查看的商品及其权益数量。
			summaryOrder() {
				return {
					...this.currentOrder,
					products: [this.currentProduct],
					benefitCount: this.currentProduct.benefitCount
				}
			},
			benefitList() {
				return this.benefitRecords.map(item => this.formatBenefit(item))
			}
		},
		created() {
			this.loadDetail()
		},
		methods: {
			async loadDetail() {
				if (!this.$route.query.orderId || !this.$route.query.productId) return
				this.loading = true
				try {
					const response = await queryMyGoodsBenefitDetail({
						orderId: this.$route.query.orderId,
						sourceGoodsId: this.$route.query.productId
					})
					const data = response.data || {}
					const sourceBenefits = Array.isArray(data.benefits) ? data.benefits : []
					this.sourceBenefitRecords = sourceBenefits
					this.benefitRecords = Array.isArray(data.benefitSummaries) ? data.benefitSummaries : sourceBenefits
					this.useRecords = Array.isArray(data.useRecords) ? data.useRecords : []
				} finally {
					this.loading = false
				}
			},
			formatBenefit(item) {
				const unit = item.benefitUnit || ''
				const total = Number(item.totalQuota || 0)
				const used = Number(item.usedQuota || 0)
				const remain = Number(item.remainQuota || 0)
				const sourceTotal = this.sourceBenefitRecords
					.filter(record => record.memberBenefit === item.memberBenefit && record.measureType === item.measureType)
					.reduce((sum, record) => sum + Number(record.totalQuota || 0), 0)
				const periodBenefit = item.measureType === 'PERIOD'
				const expireTime = item.expireTime || ''
				const expireParts = expireTime.split(' ')
				return {
					module: item.memberBenefitGroup || '-',
					type: item.memberBenefitName || item.memberBenefit || '-',
					description: item.benefitDescription || '-',
					total: `${total}${unit}`,
					sourceTotal: `${sourceTotal}${unit}`,
					expireTime: expireTime || '长期有效',
					expireDate: expireParts[0] || '长期有效',
					expireClock: expireParts.slice(1).join(' '),
					periodBenefit,
					usage: periodBenefit ? (item.lastUseTime ? '使用中' : '暂未使用') : `已使用${used}${unit}`,
					remaining: periodBenefit ? `有效至${item.expireTime || '长期'}` : `剩余${remain}${unit}`,
					remainingPrimary: periodBenefit ? '有效期内' : `${remain}${unit}`,
					remainingSecondary: periodBenefit ? (expireTime ? '截止日期见左侧' : '长期有效') : '可用额度',
					percentage: periodBenefit || total <= 0 ? 0 : Math.min(100, Math.round((used / total) * 100))
				}
			},
			// 使用同一订单号进入权益使用记录，保证上下文一致。
			viewUsageRecord() {
				this.$router.push({
					name: 'MyBenefitUsageRecord',
					query: { ...this.$route.query }
				})
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import './benefit-detail.scss';
</style>
